/*
  Dip content-page floating CTA (<dip-page-cta>).

  Rendered by sections/dip-cta-button.liquid on the content pages (story, standard, ingredients,
  tips, reviews, FAQ): the homepage hero's offer button, floating in the shared sticky-bar chrome
  once the reader scrolls past the hero, always pointing at the bundle builder.

  All reveal/address-bar behaviour comes from @theme/dip-sticky-bar (the ONE implementation; its
  Chrome-Android compensation must never be duplicated). This element only adds one thing on top:
  the bar suppresses itself while the page's own closing CTA button is on screen, so the same
  offer never shows twice at once.
*/
import { initStickyBar } from '@theme/dip-sticky-bar';

class DipPageCta extends HTMLElement {
  /** @type {HTMLElement | null} */
  #bar = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {(() => void) | null} */
  #onScroll = null;

  connectedCallback() {
    const bar = this.querySelector('.dip-stickybar');
    if (!bar) return;
    // Reveal once the reader is ~3/4 past the anchor. data-anchor names it explicitly
    // (the homepage floats after its bundle builder, store review 3 item 3); content
    // pages fall through to their hero.
    const anchor =
      (this.dataset.anchor && document.querySelector(this.dataset.anchor)) ||
      document.querySelector('.dip-phero') ||
      document.querySelector('.dip-faq-hero') ||
      document.querySelector('main section');
    this.#bar = initStickyBar({ bar, anchor });
    if (!this.#bar) return;

    // Optional first stage (data-skip-target): the bar is a jump link down the page until the
    // reader reaches that section, then the offer. One passive scroll listener, rAF-throttled.
    const skipTarget = this.dataset.skipTarget && document.querySelector(this.dataset.skipTarget);
    if (skipTarget) {
      const bar = this.#bar;
      let queued = false;
      const update = () => {
        queued = false;
        bar.classList.toggle('is-past-skip', skipTarget.getBoundingClientRect().top < window.innerHeight * 0.6);
      };
      this.#onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      update();
    }

    // Hide the float while the closing button (this section) is in view.
    const host = this.closest('section') || this;
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) this.#bar?.classList.toggle('is-suppressed', e.isIntersecting);
      },
      { rootMargin: '0px 0px 0px 0px' }
    );
    this.#io.observe(host);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    this.#bar?.remove(); // the bar was re-parented to <body>
    this.#bar = null;
  }
}

if (!customElements.get('dip-page-cta')) {
  customElements.define('dip-page-cta', DipPageCta);
}
